import React, { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import './HotelStatusPage.css';

/**
 * HotelStatusPage
 * Displays the current status of hotel registration
 * Shows appropriate messages and actions based on status (pending/declined)
 */
const HotelStatusPage = () => {
  const { currentUser, hotelStatus, hotelStatusLoading, refreshHotelStatus } = useAuth();
  const navigate = useNavigate();
  const hasRedirected = useRef(false);

  // Redirect approved users only - only once to prevent loops
  // Note: Users should arrive here with proper status already loaded
  useEffect(() => {
    if (!hotelStatusLoading && !hasRedirected.current) {
      if (hotelStatus?.status === 'approved') {
        console.log('✅ Status page: User approved, redirecting to dashboard');
        hasRedirected.current = true;
        navigate('/owner', { replace: true });
      }
    }
  }, [hotelStatus, hotelStatusLoading, navigate]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (hotelStatusLoading) {
    return (
      <div className="hotel-status-page">
        <div className="status-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading your registration status...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!hotelStatus || hotelStatus.status === 'not_registered') {
    return (
      <div className="hotel-status-page">
        <div className="status-container">
          <div className="status-message no-registration">
            <h2>No Hotel Registered</h2>
            <p>You have not registered a hotel yet. Please use the dashboard to start your registration when ready.</p>
          </div>
          <div className="status-card error">
            <h2>No Registration Found</h2>
            <p>We couldn't find a hotel registration for your account.</p>
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/owner/register')}
            >
              Register Your Hotel
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-status-page">
      <button 
        className="back-button"
        onClick={() => navigate('/owner')}
        style={{
          position: 'absolute',
          top: '20px',
          left: '20px',
          background: 'rgba(255, 255, 255, 0.9)',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.2s'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.transform = 'translateX(-4px)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.transform = 'translateX(0)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
      >
        ← Back to Dashboard
      </button>
      <div className="status-container">
        {/* Pending Status */}
        {hotelStatus.status === 'pending' && (
          <div className="status-card pending">
            <div className="status-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <path d="M12 6v6l4 2" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h1>Registration Under Review</h1>
            <h2 className="hotel-name">{hotelStatus.hotel_name || hotelStatus.hotelName}</h2>
            
            <div className="status-info">
              <p className="status-message">
                Thank you for submitting your hotel registration! Our team is currently reviewing your application and supporting documents.
              </p>
              
              <div className="status-timeline">
                <div className="timeline-item completed">
                  <div className="timeline-icon">✓</div>
                  <div className="timeline-content">
                    <h3>Application Submitted</h3>
                    <p>{formatDate(hotelStatus.submitted_at || hotelStatus.submittedAt)}</p>
                  </div>
                </div>
                
                <div className="timeline-item active">
                  <div className="timeline-icon">⏳</div>
                  <div className="timeline-content">
                    <h3>Under Review</h3>
                    <p>Our admin team is reviewing your documents</p>
                  </div>
                </div>
                
                <div className="timeline-item">
                  <div className="timeline-icon">🎉</div>
                  <div className="timeline-content">
                    <h3>Approval Decision</h3>
                    <p>You'll be notified once reviewed</p>
                  </div>
                </div>
              </div>

              <div className="info-box">
                <h3>What happens next?</h3>
                <ul>
                  <li>Our team will verify your documents and hotel information</li>
                  <li>This typically takes 2-3 business days</li>
                  <li>You'll receive an email notification once approved</li>
                  <li>After approval, you can access your full dashboard</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Declined Status */}
        {hotelStatus.status === 'declined' && (
          <div className="status-card declined">
            <div className="status-icon error">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2"/>
                <path d="M15 9l-6 6M9 9l6 6" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h1>Registration Declined</h1>
            <h2 className="hotel-name">{hotelStatus.hotel_name || hotelStatus.hotelName}</h2>
            
            <div className="status-info">
              <p className="status-message">
                Unfortunately, your hotel registration was not approved at this time.
              </p>

              <div className="decline-reason">
                <h3>Reason for Decline:</h3>
                <p>{hotelStatus.decline_reason || hotelStatus.declineReason}</p>
              </div>

              <div className="status-details">
                <div className="detail-item">
                  <span className="label">Submitted:</span>
                  <span className="value">{formatDate(hotelStatus.submitted_at || hotelStatus.submittedAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Reviewed:</span>
                  <span className="value">{formatDate(hotelStatus.reviewed_at || hotelStatus.reviewedAt)}</span>
                </div>
              </div>

              <div className="info-box">
                <h3>What you can do:</h3>
                <ul>
                  <li>Review the decline reason carefully</li>
                  <li>Prepare the necessary corrections or additional documents</li>
                  <li>Submit a new registration with the updated information</li>
                  <li>Contact support if you need clarification</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotelStatusPage;
